import {
  Badge, Button, Card, DataTable, ErrorState, Field, Input, Notice, PageHeader, PasswordInput, Permissions, Row, Stack,
  Textarea, useApi, useClampedPage, useI18n, useLimits, useSession, type BackupOperationResponse, type BackupResponse, type Column,
} from '@daghfal/core';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/**
 * Backing up, testing and restoring the platform (Q65, decision 2026-10-14 #4, #5).
 *
 * Every action is queued and runs in the background, one at a time; the operations list follows it. A restore takes
 * every nursery back to the backup's moment, so it asks the most: its own permission, the password again, the
 * backup's id typed out, and a reason. Once it starts, the platform closes for maintenance and this page, like every
 * other, answers that it is in maintenance until the API is back on the restored data.
 */
export function Backups() {
  const api = useApi();
  const { t, formatDateTime, formatMegabytes } = useI18n();
  const { can } = useSession();
  const queryClient = useQueryClient();
  // An operation still to finish is followed as closely as an open conversation.
  const followMs = useLimits().polling.threadMs;
  const [page, setPage] = useState(1);
  const [restoring, setRestoring] = useState<BackupResponse | null>(null);

  const operations = useQuery({
    queryKey: ['platform', 'backups', 'operations'],
    queryFn: () => api.platform.listBackupOperations({ pageSize: 20 }),
    refetchInterval: (query) => (query.state.data?.items.some(inFlight) ? followMs : false),
  });
  const running = operations.data?.items.find(inFlight) ?? null;

  const backups = useQuery({
    queryKey: ['platform', 'backups', { page }],
    queryFn: () => api.platform.listBackups({ page, pageSize: 20 }),
    placeholderData: keepPreviousData,
    refetchInterval: running ? followMs : false,
  });
  useClampedPage(page, setPage, backups.data?.backups.totalPages);
  const enabled = backups.data?.enabled ?? false;

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['platform', 'backups'] });
  const create = useMutation({ mutationFn: () => api.platform.createBackup(), onSettled: refresh });
  const verify = useMutation({ mutationFn: (id: string) => api.platform.verifyBackup(id), onSettled: refresh });

  const columns: Column<BackupResponse>[] = [
    { key: 'at', header: t('backups.column.at'), render: (b) => <span dir="ltr">{formatDateTime(b.createdAt)}</span> },
    { key: 'source', header: t('backups.column.source'), render: (b) => t(`backups.source.${b.source}`) },
    { key: 'size', header: t('backups.column.size'), numeric: true, render: (b) => t('backups.megabytes', { size: formatMegabytes(b.sizeBytes + b.mediaBytes) }) },
    {
      key: 'contents', header: t('backups.column.contents'),
      render: (b) => t('backups.contents', { rows: b.rows, files: b.mediaFiles }),
    },
    {
      key: 'verified', header: t('backups.column.verified'),
      render: (b) => !b.verification
        ? <span className="dg-muted">{t('backups.notVerified')}</span>
        : (
          <span title={[b.verification.summary, ...b.verification.problems].join('\n')}>
            <Badge tone={b.verification.passed ? 'success' : 'danger'}>
              {t(b.verification.passed ? 'backups.verified.passed' : 'backups.verified.failed')}
            </Badge>{' '}
            <span className="dg-muted dg-text-sm" dir="ltr">{formatDateTime(b.verification.at)}</span>
          </span>
        ),
    },
    {
      key: 'actions', header: '',
      render: (b) => (
        <Row>
          <Button variant="secondary" disabled={!enabled || running != null || verify.isPending}
            onClick={() => verify.mutate(b.id)}>
            {t('backups.verify')}
          </Button>
          {can(Permissions.platform.restoreBackups) && (
            <Button variant="danger" disabled={!enabled || running != null} onClick={() => setRestoring(b)}>
              {t('backups.restore')}
            </Button>
          )}
        </Row>
      ),
    },
  ];

  const operationColumns: Column<BackupOperationResponse>[] = [
    { key: 'at', header: t('backups.column.at'), render: (o) => <span dir="ltr">{formatDateTime(o.requestedAt)}</span> },
    { key: 'kind', header: t('backups.operation.kind'), render: (o) => t(`backups.kind.${o.kind}`) },
    {
      key: 'status', header: t('backups.operation.status'),
      render: (o) => <Badge tone={TONE[o.status]}>{t(`backups.status.${o.status}`)}</Badge>,
    },
    { key: 'by', header: t('backups.operation.by'), render: (o) => o.requestedByName ?? t('backups.nightly') },
    {
      key: 'message', header: t('backups.operation.message'),
      render: (o) => (
        <Stack tight>
          {o.reason && <span>{t('backups.operation.reason', { reason: o.reason })}</span>}
          {o.message && <span className="dg-muted dg-text-sm">{o.message}</span>}
        </Stack>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('backups.title')}
        lede={t('backups.lede')}
        actions={(
          <Button disabled={!enabled || running != null} busy={create.isPending} onClick={() => create.mutate()}>
            {t('backups.create')}
          </Button>
        )}
      />

      {backups.data && !enabled && <Notice tone="warning" title={t('backups.disabled.title')}>{t('backups.disabled.body')}</Notice>}
      {running && <Notice tone="info">{t(`backups.running.${running.kind}`)}</Notice>}
      {(create.error ?? verify.error) && <ErrorState error={create.error ?? verify.error} />}

      {restoring && <RestorePanel backup={restoring} onClose={() => { setRestoring(null); refresh(); }} />}

      <Card title={t('backups.list')}>
        <DataTable
          columns={columns}
          rows={backups.data?.backups.items}
          rowKey={(b) => b.id}
          caption={t('backups.list')}
          isLoading={backups.isPending}
          error={backups.error}
          onRetry={() => void backups.refetch()}
        />
        {backups.data && backups.data.backups.totalPages > 1 && (
          <Row>
            <Button variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>{t('action.previous')}</Button>
            <Button variant="ghost" disabled={page >= backups.data.backups.totalPages} onClick={() => setPage(page + 1)}>
              {t('action.next')}
            </Button>
          </Row>
        )}
      </Card>

      <Card title={t('backups.operations')}>
        <DataTable
          columns={operationColumns}
          rows={operations.data?.items}
          rowKey={(o) => o.id}
          caption={t('backups.operations')}
          isLoading={operations.isPending}
          error={operations.error}
          onRetry={() => void operations.refetch()}
        />
      </Card>
    </>
  );
}

/** The restore's confirmation: what it does, the id typed out, a reason, and the password again. */
function RestorePanel({ backup, onClose }: { backup: BackupResponse; onClose: () => void }) {
  const api = useApi();
  const { t, formatDateTime } = useI18n();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');

  const restore = useMutation({
    mutationFn: async () => {
      const stepUp = await api.passkeys.stepUp(password);
      return api.platform.restoreBackup(backup.id, { confirm, reason }, stepUp.token);
    },
    // The queued restore joins the operations, which are then followed until it ends, as a backup or a test is.
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['platform', 'backups'] }),
    onSettled: () => setPassword(''),
  });

  if (restore.isSuccess) {
    return <Notice tone="warning" title={t('backups.restore.started.title')}>{t('backups.restore.started.body')}</Notice>;
  }

  const ready = confirm.trim() === backup.id && reason.trim().length > 0 && password.length > 0;

  return (
    <Card title={t('backups.restore.title', { at: formatDateTime(backup.createdAt) })}>
      <Stack>
        <Notice tone="danger" title={t('backups.restore.warning.title')}>{t('backups.restore.warning.body')}</Notice>
        {restore.error != null && <ErrorState error={restore.error} />}
        <Field label={t('backups.restore.confirm', { id: backup.id })} required>
          {(props) => <Input {...props} dir="ltr" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" />}
        </Field>
        <Field label={t('backups.restore.reason')} required>
          {(props) => <Textarea {...props} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        <Field label={t('backups.restore.password')} required>
          {(props) => (
            <PasswordInput {...props} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          )}
        </Field>
        <Row>
          <Button variant="danger" disabled={!ready} busy={restore.isPending} onClick={() => restore.mutate()}>
            {t('backups.restore.submit')}
          </Button>
          <Button variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </Card>
  );
}

const TONE = {
  Queued: 'info', Running: 'info', Restarting: 'warning', Succeeded: 'success', Failed: 'danger',
} as const;

function inFlight(o: BackupOperationResponse) {
  return o.status === 'Queued' || o.status === 'Running' || o.status === 'Restarting';
}
